// The wish board's undo-after-delete concern, in one place.
//
// W49.0. `Wishes.tsx` grew past the 15 KB route ceiling (`AGENTS.md §3 #10`:
// 「超就当场拆」) after P2-4 added a restore-permission check to it. This is the
// extraction, and it is the right seam: "a wish was deleted, and here is what
// this viewer can do about it" is one behaviour with one set of rules, and it
// has nothing to do with rendering four kanban columns.
//
// The rule it exists to express, and which is easy to get wrong when it is
// inline: **`delete_wish` is open to either partner; `restore_wish` is
// author-only.** So the person who most wants the undo button — the one who
// just deleted their partner's wish — is the one person the server will
// refuse. `restorable` decides whether the affordance appears at all, and a
// refused restore says so instead of leaving the bar implying the row is
// coming back.

import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/toast";
import { useUndoBar } from "@/hooks/useUndoBar";
import { ApiError } from "@/lib/api/pair";
import { appErrorMessage } from "@/lib/i18n/app";
import { restoreWish } from "@/lib/api/wishlist";

export interface WishDeletedPayload {
  id: string;
  name: string;
  restorable: boolean;
}

export function useWishRestore() {
  const queryClient = useQueryClient();
  const { push } = useToast();

  // W39.1 R1 (ADR-0080). No `onExpire`: a wish that is not undone simply stays
  // deleted for its restore window, and there is no page to navigate away from.
  const undo = useUndoBar<WishDeletedPayload>();

  const restoreMut = useMutation({
    mutationFn: (id: string) => restoreWish(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
    },
    // W49.0 P2-4. Without this a 403 produced no signal at all: the undo bar
    // stayed up, implying the wish was coming back. The reporter deliberately
    // skips call sites that write their own `onError`, so the toast is ours to
    // push — and it uses the same mapping so the wording matches everywhere.
    onError: (err: unknown) => {
      undo.clear();
      push({
        message:
          err instanceof ApiError
            ? appErrorMessage(err.code, err.message)
            : "還原失敗，請稍後再試。",
        variant: "danger",
      });
    },
  });

  /** Hand the deleted wish up; the sheet cannot host the bar itself. */
  const onDeleted = useCallback((payload: WishDeletedPayload) => undo.begin(payload), [undo]);

  /** `undefined` for someone else's wish — the bar then renders no button. */
  const onUndo = useCallback(() => {
    if (!undo.item?.restorable) return;
    restoreMut.mutate(undo.item.id);
    undo.clear();
  }, [undo, restoreMut]);

  return { item: undo.item, onDeleted, onUndo };
}
